<template lang='pug'>
.newsletter-signup(ref="root" :class="{ 'stand-alone': standAlone }")
  h5(v-if="!standAlone") {{ __('NEWSLETTER SIGNUP') }}
  p(:class="{ 'font-size-xxs': !standAlone }")
    | {{ __('Sign up for our Newsletter with Rogue News and Product Releases.') }}
  form(@submit.prevent="subscribe()")
    RogueInput#newslettersignup-email(
      label="Email address"
      :placeholder='__("Email")'
      type='email'
      autocomplete="email"
      :required="true"
      aria-required="true"
      :name='formConfig.email'
      v-model='email'
      @focus='showForm = true'
    )
    //- Mounted on demand - the footer renders on every page, but none of this matters
    //- until someone starts signing up
    div(v-if='showForm || standAlone')
      p(:class="{ 'font-size-xxs': !standAlone }")
        | {{__('Choose your subscription preferences below. Sign up for both to get all our updates straight to your inbox.')}}
      p.select-list-message(v-if="noneSelected" :class="{ 'font-size-xxs': !standAlone }")
        | {{__('Please select a list to sign up.')}}
      .checkbox-container
        Checkbox(
          v-for="newsletter in newsletters"
          :key="newsletter.id"
          :id="'newsletter-'+newsletter.id"
          :label="newsletter.label"
          v-model="newsletter.selected"
        )
          Tooltip(v-if="newsletter.tooltip")
            .whats-this {{ __('What\'s This?') }}
            template(#tooltip) {{ __(newsletter.tooltip) }}
      RogueButton(size="s" :loading="loading" :disabled="loading" @click="subscribe()") {{ __('Sign Up') }}
      div(aria-live="polite")
        Message.status-msg(
          v-if="statusMessage"
          :status="statusMessage.status"
          :icon="statusMessage.icon"
          :message="statusMessage.text"
        )
  policies
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import RogueInput from 'sisu/components/forms/RogueInput.vue'
import { useRogue } from '@/js/services/Rogue'
import Policies from '@/js/pages/checkout/components/policies.vue'
import { useNewsletterSignup } from '@/js/composables/useNewsletterSignup'

const rogue = useRogue()

withDefaults(defineProps<{
  standAlone?: boolean,
}>(), {
  standAlone: false,
})

const showForm = ref(false)

const {
  email,
  loading,
  newsletters,
  noneSelected,
  statusMessage,
  subscribe,
} = useNewsletterSignup({
  // The form sits in the footer, already in view - scrolling to the error would yank the page
  scrollToError : false,
  resetAfterMs  : 3000,
  onReset       : () => showForm.value = false,
})

const formConfig = computed(() => rogue.site.forms?.newsletters || {})
</script>

<style lang='scss' scoped>
.newsletter-signup:not(.stand-alone) {
  p {
    margin-bottom: $space-xs;
    color: $color-gray-dark;

    &.select-list-message {
      color: $color-red;
    }
  }
}

// Screen-reader-only email label; the approved design shows only the placeholder
.newsletter-signup :deep(.field > .label) {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;

  // The input's required attribute already announces "required"
  .required {
    display: none;
  }
}

.button {
  margin-bottom: $space-s;
}

.checkbox-container {
  position: relative;
}

.checkbox {
  margin-bottom: $space-xs;
  color: $color-white;
  font-size: $size-xxs;
  line-height: 1;

  :deep(.primary) {
    flex-grow: 0;
    margin-right: $space-xs;
  }
}

.whats-this {
  display: inline-block;
  text-decoration: underline;
  cursor: pointer;
  font-size: $size-xxs;
  color: $color-gray-dark;
  line-height: 1;
}

.stand-alone {
  font-size: $size-s;

  .checkbox {
    color: $color-black;
    font-size: $size-s;
  }
}

.status-msg {
  margin-bottom: $size-xs;
  color: $color-black;
}
</style>
